// ============================================
// CONFIGURATION - Company Presets & VAT Rates
// ============================================

import { CountryCode, CurrencyCode, CompanyInfo, AppSettings, PrintLocale } from '../types';

// VAT Rates by Country
export const vatRates: Record<CountryCode, number> = {
  fi: 0.255,  // Finland 25.5%
  se: 0.25,   // Sweden 25%
  no: 0.25,   // Norway 25%
  dk: 0.25,   // Denmark 25%
  pl: 0.23,   // Poland 23%
  nl: 0.21,   // Netherlands 21%
  be: 0.21,   // Belgium 21%
  gb: 0.20,   // United Kingdom 20%
  at: 0.20,   // Austria 20%
  fr: 0.20,   // France 20%
  tr: 0.20,   // Turkey 20%
  de: 0.19,   // Germany 19%
};

// Currency Codes by Country
export const currencyCodes: Record<CountryCode, CurrencyCode> = {
  fi: 'EUR',  // Finland
  se: 'SEK',  // Sweden
  no: 'NOK',  // Norway
  dk: 'DKK',  // Denmark
  pl: 'PLN',  // Poland
  nl: 'EUR',  // Netherlands
  de: 'EUR',  // Germany
  at: 'EUR',  // Austria
  be: 'EUR',  // Belgium
  fr: 'EUR',  // France
  gb: 'GBP',  // United Kingdom
  tr: 'TRY',  // Turkey
};

// Locale Mapping for Intl Formatting
export const localeMapping: Record<CountryCode, string> = {
  fi: 'fi-FI',
  se: 'sv-SE',
  no: 'nb-NO',
  dk: 'da-DK',
  pl: 'pl-PL',
  nl: 'nl-NL',
  de: 'de-DE',
  at: 'de-AT',
  be: 'nl-BE',
  fr: 'fr-FR',
  gb: 'en-GB',
  tr: 'tr-TR',
};

// Company Information Presets
export const companyPresets: Record<CountryCode, CompanyInfo> = {
  fi: {
    name: 'Halton Oy',
    vatId: '1840434-5',
    address: 'Haltonintie 1-3',
    postalCode: '47400',
    city: 'Kausala',
    country: 'Finland',
    vatRate: 0.255,
  },
  se: {
    name: 'Halton AB',
    vatId: '556214-9087',
    address: 'Torshamnsgatan 33A',
    postalCode: 'SE 164 40',
    city: 'Kista',
    country: 'Sweden',
    vatRate: 0.25,
  },
  no: {
    name: 'Halton AS',
    vatId: '943277184',
    address: 'Lahaugmoveien 81',
    postalCode: '2013',
    city: 'Skjetten',
    country: 'Norway',
    vatRate: 0.25,
  },
  dk: {
    name: 'Halton A/S',
    vatId: '11744281',
    address: 'Husby Allé 17, 1. sal',
    postalCode: '2630',
    city: 'Taastrup',
    country: 'Denmark',
    vatRate: 0.25,
  },
  pl: {
    name: 'Halton Sp. Z.o.o.',
    vatId: '0000017439',
    address: 'ul. Żegańska 2A',
    postalCode: '04-713',
    city: 'Warszawa',
    country: 'Poland',
    vatRate: 0.23,
  },
  nl: {
    name: 'Halton B.V.',
    vatId: '30102232',
    address: 'Van Rietlaan 16A',
    postalCode: '3461 HW',
    city: 'Linschoten',
    country: 'Netherlands',
    vatRate: 0.21,
  },
  de: {
    name: 'Halton Foodservice GmbH',
    vatId: 'HRB 9023',
    address: 'Tiroler Strasse 60',
    postalCode: '83242',
    city: 'Reit im Winkl',
    country: 'Germany',
    vatRate: 0.19,
  },
  at: {
    name: 'Halton Foodservice GmbH',
    vatId: 'HRB 9023',
    address: 'Tiroler Strasse 60',
    postalCode: '83242',
    city: 'Reit im Winkl',
    country: 'Austria',
    vatRate: 0.20,
  },
  be: {
    name: 'Halton N.V./S.A.',
    vatId: 'BE0444841307',
    address: 'Researchpark Haasrode 1529, Interleuvenlaan 62',
    postalCode: '3001',
    city: 'Leuven',
    country: 'Belgium',
    vatRate: 0.21,
  },
  fr: {
    name: "Diffus'air Service SAS",
    vatId: '408 645 901',
    address: 'Zone Technoparc Futura, CS 80102',
    postalCode: '62402',
    city: 'Bethune',
    country: 'France',
    vatRate: 0.20,
  },
  gb: {
    name: 'Halton UK',
    vatId: 'TBD',
    address: 'TBD',
    postalCode: 'TBD',
    city: 'TBD',
    country: 'United Kingdom',
    vatRate: 0.20,
  },
  tr: {
    name: 'Halton Havalandirma Sistemleri Limited Sirketi',
    vatId: '4560568202',
    address: 'FSM Mahallesi Poligon Cad. Buyaka Kule 2 Sitesi No:8, C Blok, Kat:17',
    postalCode: '34771',
    city: 'Ümraniye, Istanbul',
    country: 'Turkey',
    vatRate: 0.20,
  },
};

// Print Languages
export const printLanguages: { code: PrintLocale; name: string; nativeName: string }[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
];

// Countries List
export const countries: { code: CountryCode; name: string }[] = [
  { code: 'fi', name: 'Finland - Halton Oy' },
  { code: 'se', name: 'Sweden - Halton AB' },
  { code: 'no', name: 'Norway - Halton AS' },
  { code: 'dk', name: 'Denmark - Halton A/S' },
  { code: 'pl', name: 'Poland - Halton Sp. Z.o.o.' },
  { code: 'nl', name: 'Netherlands - Halton B.V.' },
  { code: 'de', name: 'Germany - Halton Foodservice GmbH' },
  { code: 'at', name: 'Austria - Halton Foodservice GmbH' },
  { code: 'be', name: 'Belgium - Halton N.V./S.A.' },
  { code: 'fr', name: "France - Diffus'air Service SAS" },
  { code: 'gb', name: 'United Kingdom - Halton UK' },
  { code: 'tr', name: 'Turkey - Halton Havalandirma' },
];

// Default Settings
export const defaultSettings: AppSettings = {
  uiLanguage: 'en',
  printLanguage: 'en',
  companyCountry: 'fi',
  paymentTermsDays: 21,
  showLogo: true,
  accommodationRate: 160,
  perDiemRate: 51,
  defaultHourlyRate: 95,
  defaultDistanceRate: 0.70,
  defaultTimeRate: 65,
  serviceBranchAddress: '',
  serviceBranchName: '',
  googleMapsApiKey: '',
  // Job number system
  userIdentityNumber: 1, // Default user identity (1-9)
};

// Hourly Rate Options
export const hourlyRateOptions = [
  { value: 95, label: '95 €/h - Standard' },
  { value: 105, label: '105 €/h' },
  { value: 115, label: '115 €/h' },
  { value: 120, label: '120 €/h - Senior' },
];

// Call-out Fee Options
export const callOutFeeOptions = [
  { value: 0, label: 'No charge' },
  { value: 60, label: '60 €' },
  { value: 80, label: '80 €' },
  { value: 120, label: '120 €' },
  { value: -1, label: 'Custom' },
];
